import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  Stack,
  Pagination,
  TableSortLabel,
  useTheme
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { useGetPaymentList } from "../../hooks/api/usePayment";
import NoPaymentFound from "./NoPaymentFound";
import PaymentDetailModal from "./PaymentDetailModal";
import { Payment } from "./types/payment";
import { PaymentMethodLabels } from "./enums/paymentMethod";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  BRAND_COLOR,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { formatDate } from "../Orders/utils/orderUtils";
import { useFormatCurrency } from "../../hooks/useFormatCurrency";
import ListPageToolbar from "../common/ListPageToolbar";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import { useReportError } from "../../hooks/useReportError";

type Order = "asc" | "desc";

const PaymentList: React.FC = () => {
  const { t } = useKivunovaTranslation();
  const formatCurrency = useFormatCurrency();

  const { paymentList } = useSelector((state: RootState) => state.payments);
  const useGetPaymentListMutation = useGetPaymentList();
  const reportError = useReportError();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const [fetchDone, setFetchDone] = useState(false);

  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [detailPaymentId, setDetailPaymentId] = useState<string | null>(null);
  const [order, setOrder] = useState<Order>("desc");
  type PaymentSortKey = keyof Payment | "createdAt" | "updatedAt";
  const [orderBy, setOrderBy] = useState<PaymentSortKey>("createdAt");
  const itemsPerPage = 10;

  useEffect(() => {
    (async () => {
      try {
        await useGetPaymentListMutation.mutateAsync();
      } catch (err) {
        reportError("Failed to fetch payment list (UI)", err, {
          component: "PaymentList"
        });
      } finally {
        setFetchDone(true);
      }
    })();
  }, [reportError]);

  /** Filter by search */
  const filteredList = useMemo(() => {
    if (!search.trim()) return paymentList;
    return paymentList.filter(
      (payment) =>
        payment.paymentNumber.toLowerCase().includes(search.toLowerCase()) ||
        payment.invoiceId.toLowerCase().includes(search.toLowerCase()) ||
        payment.paymentMethod.toLowerCase().includes(search.toLowerCase()) ||
        payment.transactionReference
          ?.toLowerCase()
          .includes(search.toLowerCase())
    );
  }, [paymentList, search]);

  /** Sorting logic */
  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      const aValue = a[orderBy] ?? "";
      const bValue = b[orderBy] ?? "";
      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      if (typeof aValue === "string" && typeof bValue === "string") {
        if (
          orderBy === "createdAt" ||
          orderBy === "updatedAt" ||
          orderBy === "paymentDate"
        ) {
          const aDate = new Date(aValue).getTime();
          const bDate = new Date(bValue).getTime();
          return order === "asc" ? aDate - bDate : bDate - aDate;
        }
      }
      return order === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredList, order, orderBy]);

  /** Pagination */
  const paginatedList = useMemo(() => {
    const startIndex = (page - 1) * itemsPerPage;
    return sortedList.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedList, page]);

  const handleSort = (property: PaymentSortKey) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  if (!fetchDone) {
    return (
      <CommonLoader
        loadingMessage={t("payment-list-loading", "Loading payments...")}
      />
    );
  }

  if (paymentList.length === 0) {
    return <NoPaymentFound />;
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        display: "flex",
        flexDirection: "column",
        gap: 3
      }}
    >
      <ListPageToolbar
        title={t("payments", "Payments")}
        icon={<PaymentOutlinedIcon sx={{ color: BRAND_COLOR, fontSize: 22 }} />}
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t(
          "search-payments-placeholder",
          "Search by amount, payment method, payment date, or transaction reference."
        )}
        isMobile={matchMD}
      />

      {/* Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "amount"}
                  direction={orderBy === "amount" ? order : "asc"}
                  onClick={() => handleSort("amount")}
                >
                  {t("amount", "Amount")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "paymentMethod"}
                  direction={orderBy === "paymentMethod" ? order : "asc"}
                  onClick={() => handleSort("paymentMethod")}
                >
                  {t("payment-method", "Payment Method")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "paymentDate"}
                  direction={orderBy === "paymentDate" ? order : "asc"}
                  onClick={() => handleSort("paymentDate")}
                >
                  {t("payment-date", "Payment Date")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "transactionReference"}
                  direction={orderBy === "transactionReference" ? order : "asc"}
                  onClick={() => handleSort("transactionReference")}
                >
                  {t("transaction-reference", "Transaction Reference")}
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "createdAt"}
                  direction={orderBy === "createdAt" ? order : "asc"}
                  onClick={() => handleSort("createdAt")}
                >
                  {t("created", "Created")}
                </TableSortLabel>
              </TableCell>
              <TableCell align="center">{t("actions", "Actions")}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedList.map((payment) => (
              <TableRow key={(payment as Payment & { id: string }).id} hover>
                <TableCell sx={{ fontSize: 13 }}>
                  {formatCurrency(payment.amount)}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {PaymentMethodLabels[payment.paymentMethod] ||
                    payment.paymentMethod}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {formatDate(payment.paymentDate)}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {payment.transactionReference || t("not-applicable", "N/A")}
                </TableCell>
                <TableCell sx={{ fontSize: 13 }}>
                  {formatDate(
                    (payment as Payment & { createdAt: string }).createdAt
                  )}
                </TableCell>
                <TableCell align="center" sx={{ fontSize: 13 }}>
                  <IconButton
                    color="primary"
                    aria-label={t("view-payment", "View Payment")}
                    onClick={() =>
                      setDetailPaymentId(
                        (payment as Payment & { id: string }).id
                      )
                    }
                  >
                    <VisibilityOutlinedIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      {sortedList.length > itemsPerPage && (
        <Stack spacing={2} alignItems="center" mt={3}>
          <Pagination
            count={Math.ceil(sortedList.length / itemsPerPage)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Stack>
      )}

      <PaymentDetailModal
        open={detailPaymentId !== null}
        paymentId={detailPaymentId}
        onClose={() => setDetailPaymentId(null)}
      />
    </Box>
  );
};

export default PaymentList;
